<script setup>
import { onMounted, reactive, computed } from "vue";
import { useFetch } from "@/plugins/fetch";
import { useSettingStore } from "@/store/SettingsStore";

// Import new and existing components
import StatCard from "@/components/Dashboard/Cards/StatCard.vue";
import RecentActivityCard from "@/components/Dashboard/Cards/RecentActivityCard.vue";
import GradientLineChart from "@/components/Dashboard/Charts/GradientLineChart.vue";
import GetStartedCard from "@/components/Account/GetStartedCard.vue";
import Skeleton from "primevue/skeleton";

const fetch = useFetch();
const settingsStore = useSettingStore();

// Centralized state for the dashboard
const state = reactive({
  loading: true,
  stats: {
    online: 0,
    offline: 0,
    inAlert: 0,
    total: 0,
  },
  chart: {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    data: Array(12).fill(0),
    trend: '',
  },
  activity: [], // Mock data, to be replaced with API call
});

// SVG icons for the StatCards
const icons = {
  online: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z",
  inAlert: "M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z",
  offline: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zM8 11h8v2H8z",
  total: "M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z",
};

// Computed property for the Get Started card
const isGetStartedShowing = computed(() => settingsStore.isGetStartedShown);

// Consolidated data fetching
const fetchDashboardData = async () => {
  state.loading = true;
  try {
    // Use Promise.allSettled to handle individual promise failures
    const [statsResult, trendsResult, averagesResult, activityResult] = await Promise.allSettled([
      fetch('sensors/stats/'),
      fetch('sensors/trends/temperature/'),
      fetch('sensors/averages/'),
      // Mocking activity, replace with real API call: fetch('activity/recent')
      Promise.resolve([
        { type: 'alert', title: 'High Temp Alert', description: 'Sensor "Greenhouse" at 45°C', time: '2 min ago' },
        { type: 'offline', title: 'Sensor Offline', description: 'Sensor "Server Room" lost connection', time: '1 hr ago' },
        { type: 'online', title: 'Sensor Online', description: 'Sensor "Basement" reconnected', time: '3 hr ago' },
      ])
    ]);

    // Check if stats were fetched successfully before processing
    if (statsResult.status === 'fulfilled') {
      // Handle wrapped API response
      const response = statsResult.value;
      const stats = response?.data || response || {};
      state.stats = {
        online: stats.total_online ?? 0,
        offline: stats.total_offline ?? 0,
        inAlert: stats.total_alert ?? 0,
        total: stats.total_count ?? 0,
      };
    } else {
      console.error("Failed to fetch stats:", statsResult.reason);
    }

    // Check if trends were fetched successfully
    if (trendsResult.status === 'fulfilled' && trendsResult.value) {
      const response = trendsResult.value;
      const trends = response?.data || response || {};
      if (trends.trend_messages && trends.trend_messages.length > 0) {
        state.chart.trend = trends.trend_messages[Math.floor(Math.random() * trends.trend_messages.length)];
      }
    } else {
      console.error("Failed to fetch trends:", trendsResult.reason);
    }

    // Check if averages were fetched successfully
    if (averagesResult.status === 'fulfilled' && averagesResult.value) {
      const response = averagesResult.value;
      const averages = response?.data || response || {};
      const monthMap = { '01': 0, '02': 1, '03': 2, '04': 3, '05': 4, '06': 5, '07': 6, '08': 7, '09': 8, '10': 9, '11': 10, '12': 11 };
      if (averages.message !== 'No sensors to show averages for') {
        Object.keys(averages).forEach(date => {
          if (averages[date] !== null && date.includes('-')) {
            const month = date.split('-')[1];
            if (monthMap[month] !== undefined) {
              state.chart.data[monthMap[month]] = averages[date];
            }
          }
        });
      }
    } else {
      console.error("Failed to fetch averages:", averagesResult.reason);
    }

    // Check if activity was fetched successfully
    if (activityResult.status === 'fulfilled') {
      state.activity = activityResult.value;
    } else {
      console.error("Failed to fetch activity:", activityResult.reason);
    }

  } catch (error) {
    // This will now only catch synchronous errors, not the API failures
    console.error("An unexpected error occurred in fetchDashboardData:", error);
  } finally {
    state.loading = false;
  }
};

onMounted(fetchDashboardData);
</script>
<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <!-- Stat Cards -->
      <div class="col-xl-3 col-sm-6 mb-xl-0 mb-4">
        <StatCard title="Sensors Online" :value="state.stats.online" :icon="icons.online" color="success" />
      </div>
      <div class="col-xl-3 col-sm-6 mb-xl-0 mb-4">
        <StatCard title="In Alert" :value="state.stats.inAlert" :icon="icons.inAlert" color="warning" />
      </div>
      <div class="col-xl-3 col-sm-6 mb-xl-0 mb-4">
        <StatCard title="Sensors Offline" :value="state.stats.offline" :icon="icons.offline" color="danger" />
      </div>
      <div class="col-xl-3 col-sm-6 mb-xl-0 mb-4">
        <StatCard title="Total Sensors" :value="state.stats.total" :icon="icons.total" color="primary" />
      </div>
    </div>

    <div class="row mt-4">
      <!-- Main Content Column -->
      <div class="col-lg-8 mb-4 mb-lg-0">
        <div class="card z-index-2">
          <gradient-line-chart
              v-if="!state.loading"
              id="chart-line"
              title="Temperature Overview"
              :description="state.chart.trend"
              :chart="{
              labels: state.chart.labels,
              datasets: [{
                label: 'Average Temperature',
                data: state.chart.data,
              }],
            }"
          />
          <div v-else class="p-4">
            <Skeleton width="30%" height="2rem" class="mb-4" />
            <Skeleton width="50%" height="1rem" class="mb-4" />
            <Skeleton height="15rem" />
          </div>
        </div>
      </div>
      <!-- Side Column -->
      <div class="col-lg-4">
        <RecentActivityCard :items="state.activity" />
      </div>
    </div>

    <div class="row mt-4">
      <div class="col-12">
        <GetStartedCard v-if="isGetStartedShowing" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.p-skeleton::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  transform: translateX(-100%);
  background-image: linear-gradient(90deg, rgba(255, 255, 255, 0) 0, rgba(255, 255, 255, 0.2) 20%, rgba(255, 255, 255, 0.5) 60%, rgba(255, 255, 255, 0));
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}
</style>